/**
 * Theme, language, and the way into settings.
 *
 * Two circles, one tap each, sitting under the signed-in user in the sidebar footer.
 *
 * These used to be two segmented pickers in a bordered strip of their own, which gave a
 * pair of twice-a-year settings the same visual weight as the navigation. Each control
 * has exactly two useful states, so a toggle says it in a quarter of the space, and the
 * landing page uses the same pair of circles — somebody who sees both should not have to
 * learn two ways to change the language.
 *
 * Theme has no third "system" option here either. It starts on the system preference,
 * and the first tap pins the opposite of whatever is currently showing.
 */

import { NavLink } from 'react-router-dom';

import { useAuth } from '@/features/auth/AuthProvider';
import { useI18n, type Locale } from '@/i18n';
import { useTheme } from '@/lib/theme';

function SunIcon() {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
      <circle cx="10" cy="10" r="3.4" />
      <path d="M10 2.2v1.7M10 16.1v1.7M17.8 10h-1.7M3.9 10H2.2M15.5 4.5l-1.2 1.2M5.7 14.3l-1.2 1.2M15.5 15.5l-1.2-1.2M5.7 5.7 4.5 4.5" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round">
      <path d="M16.5 11.8A7 7 0 1 1 8.2 3.5a5.6 5.6 0 0 0 8.3 8.3Z" />
    </svg>
  );
}

/** A gear, generated rather than eyeballed — same eight teeth as the public site's. */
const GEAR =
  'M18.41 8.21 L18.41 11.79 L16.44 11.43 L15.57 13.55 L17.21 14.68 L14.68 17.21 ' +
  'L13.55 15.57 L11.43 16.44 L11.79 18.41 L8.21 18.41 L8.57 16.44 L6.45 15.57 ' +
  'L5.32 17.21 L2.79 14.68 L4.43 13.55 L3.56 11.43 L1.59 11.79 L1.59 8.21 L3.56 8.57 ' +
  'L4.43 6.45 L2.79 5.32 L5.32 2.79 L6.45 4.43 L8.57 3.56 L8.21 1.59 L11.79 1.59 ' +
  'L11.43 3.56 L13.55 4.43 L14.68 2.79 L17.21 5.32 L15.57 6.45 L16.44 8.57 Z';
const HOLE = 'M10 6.6 A3.4 3.4 0 1 0 10 13.4 A3.4 3.4 0 1 0 10 6.6 Z';

const CIRCLE =
  'w-8 h-8 inline-flex items-center justify-center rounded-full border border-border ' +
  'bg-surface text-subtle transition-colors hover:text-primary hover:border-[var(--color-primary)]';

/**
 * Sign out is the one control here that is not a preference, and it is the one that
 * cannot be undone by pressing it again — so it is red from the start rather than only
 * on hover. The other three change how the portal looks; this one ends your session.
 */
/*
 * Real tokens, not Tailwind opacity modifiers on a CSS variable. `border-[var(--x)]/45`
 * cannot be resolved — the variable holds a hex string, not channels — so the class
 * fell through to a default border that showed up as a pale ring around the button,
 * which is exactly the unexplained white outline that was reported.
 */
const CIRCLE_DANGER =
  'w-8 h-8 inline-flex items-center justify-center rounded-full ' +
  'border border-[var(--color-danger-bg)] bg-[var(--color-danger-bg)] ' +
  'text-[var(--color-danger)] transition-colors ' +
  'hover:bg-[var(--color-danger)] hover:border-[var(--color-danger)] hover:text-white';

function SignOutIcon() {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true" className="w-4 h-4 rtl:-scale-x-100" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12.3 6.1V4.4a1.4 1.4 0 0 0-1.4-1.4H4.6a1.4 1.4 0 0 0-1.4 1.4v11.2a1.4 1.4 0 0 0 1.4 1.4h6.3a1.4 1.4 0 0 0 1.4-1.4v-1.7" />
      <path d="M8.2 10h8.6M14.3 7.5 16.8 10l-2.5 2.5" />
    </svg>
  );
}

/**
 * Renders nothing when nobody is signed in.
 *
 * The same toggle row appears on the sign-in page — language and theme have to be
 * reachable by someone who cannot read the form yet — and a sign-out button there
 * would be an offer to end a session that does not exist.
 */
export function SignOutButton() {
  const { t } = useI18n();
  const { signOut, status } = useAuth();

  if (status !== 'signedIn') return null;

  return (
    <button
      type="button"
      onClick={() => void signOut(null)}
      title={t('auth.signOut')}
      aria-label={t('auth.signOut')}
      className={CIRCLE_DANGER}
    >
      <SignOutIcon />
    </button>
  );
}

/**
 * `compact` is the phone's title bar, where the same three controls sit in the corner
 * next to the logo rather than spread across a sidebar footer. Same buttons, different
 * amount of room — not a different component, so they cannot drift apart.
 */
export function PreferenceToggles({
  compact = false,
  showSettings = true,
}: {
  compact?: boolean;
  /**
   * The sign-in page turns this off. Settings lives behind the gate, so a gear there
   * is a button that can only bounce you back to the page you are already on.
   */
  showSettings?: boolean;
}) {
  const { t, locale, setLocale } = useI18n();
  const { resolved, setPreference } = useTheme();

  const nextTheme = resolved === 'dark' ? 'light' : 'dark';
  // IT -> EN -> AR -> IT. One circle for three languages keeps the sidebar footer as it
  // was; the full three-way choice is in Settings.
  const nextLocale: Locale = locale === 'it' ? 'en' : locale === 'en' ? 'ar' : 'it';

  return (
    // Spread, not bunched: three controls of equal standing across the width of the
    // sidebar, under a centred name.
    <div className={compact ? 'flex items-center gap-1' : 'flex items-center justify-around px-2'}>
      {/*
        Each circle shows the CURRENT state — IT while the portal is in Italian, the sun
        while it is light — and a tap switches to the other. The tooltip says what the
        tap will do.
      */}
      <button
        type="button"
        onClick={() => setLocale(nextLocale)}
        title={`${t(`locale.${locale}`)} → ${t(`locale.${nextLocale}`)}`}
        aria-label={`${t('locale.label')}: ${t(`locale.${locale}`)}`}
        className={`${CIRCLE} text-[11px] font-bold uppercase tracking-wide`}
      >
        {locale}
      </button>

      <button
        type="button"
        onClick={() => setPreference(nextTheme)}
        title={`${t(resolved === 'dark' ? 'theme.dark' : 'theme.light')} → ${t(nextTheme === 'dark' ? 'theme.dark' : 'theme.light')}`}
        aria-label={`${t('theme.label')}: ${t(resolved === 'dark' ? 'theme.dark' : 'theme.light')}`}
        className={CIRCLE}
      >
        {resolved === 'dark' ? <MoonIcon /> : <SunIcon />}
      </button>

      {showSettings ? <SettingsLink /> : null}
      <SignOutButton />
    </div>
  );
}

/**
 * On its own, because the phone's title bar wants this and not the other two.
 *
 * Settings used to be a nav item in an admin-only group, which meant a manager saw no
 * link to it anywhere and the page was unreachable. It belongs with the per-person
 * controls, and the page itself still decides what each role may change.
 */
export function SettingsLink() {
  const { t } = useI18n();

  return (
    <NavLink
      to="/settings"
      title={t('nav.settings')}
      aria-label={t('nav.settings')}
      className={({ isActive }) =>
        `${CIRCLE} ${isActive ? 'text-primary border-[var(--color-primary)]' : ''}`
      }
    >
      <svg viewBox="0 0 20 20" aria-hidden="true" className="w-4 h-4">
        <path d={`${GEAR} ${HOLE}`} fill="currentColor" fillRule="evenodd" />
      </svg>
    </NavLink>
  );
}
